import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { NavigationBar } from '@/components/layout/NavigationBar';
import { WidthConstraint } from '@/components/layout/Section';
import { SharedLayout } from '@/components/layout/SharedLayout';
import { PageCommonProvider } from '@/lib/usePageCommon';

import type { SharedLayoutProps } from '@/components/layout/SharedLayout';
import type { SanityNavigationType } from '@/types/sanity';
import type { Navigation_exType, SiteSettings } from '@/types/shared';
import type { ReactNode } from 'react';

export interface FormLayoutProps extends SharedLayoutProps {
  contentPane?: ReactNode;
  navigation?: SanityNavigationType;
  navigations_ex?: Navigation_exType[];
  siteSettings: SiteSettings;
}

export const FormLayout = ({
  children,
  contentPane,
  localeTranslations,
  navigation,
  navigations_ex,
  siteSettings,
  ...sharedLayoutProps
}: FormLayoutProps) => {
  return (
    <PageCommonProvider siteSettings={siteSettings}>
      <SharedLayout
        localeTranslations={localeTranslations}
        {...sharedLayoutProps}
      >
        {(navigation || navigations_ex) && (
          <NavigationBar
            navigation={navigation}
            navigation_ex={navigations_ex[0]}
            siteSettings={siteSettings}
            hideCTA
            audiences={
              siteSettings?.isAudienceSwitcherEnabled &&
              navigations_ex
                .map((navigation) => navigation.audience)
                .filter((audience) => audience !== null)
            }
          />
        )}
        <Main>
          <WidthConstraint includePadding={['md', 'lg']}>
            <PageContainer>
              <ContentPane>{contentPane}</ContentPane>
              <FormPane>{children}</FormPane>
            </PageContainer>
          </WidthConstraint>
        </Main>
      </SharedLayout>
    </PageCommonProvider>
  );
};

const Main = styled.main`
  position: relative;
  display: flex;
  background: var(--background-warm-base);
  padding-top: calc(var(--full-navbar-height) + var(--spacing-large));

  ${screen.md} {
    height: 100vh;
    overflow: hidden;
  }
`;

const PageContainer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: fit-content;
  width: 100%;
  margin: 0 auto;

  ${screen.md} {
    flex-direction: row;
    gap: var(--spacing-xx-large);
    height: 100%;
    max-height: 900px;
    padding-bottom: var(--spacing-large);

    > div {
      flex: 1 1 48%;
      align-self: stretch;
    }
  }

  ${screen.lg} {
    gap: 5%;

    > div {
      align-self: stretch;
    }

    > div:first-of-type {
      flex: 1 1 50%;
      align-self: stretch;
    }
    > div:nth-of-type(2) {
      flex: 1 1 45%;
      align-self: stretch;
    }
  }

  @media (min-width: 2000px) {
    align-self: center;
  }
`;

const ContentPane = styled.div`
  position: relative;
  flex-grow: 1;
  flex-shrink: 0;
  padding: 0 var(--section-gutter) var(--spacing-large);

  ${screen.md} {
    padding: 0;
  }
`;

const FormPane = styled.div`
  height: 100%;
  position: relative;

  ${screen.md} {
    padding: 0;
    height: auto;
  }
`;
